import { AdjustmentsIcon, UserIcon } from "@heroicons/react/outline";
import { Plus, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { HUMAN_INPUT_DATA_TYPE, SMART_TABLE_HUMAN_INPUT_CONTROLS } from "./constants";
import DataTypeSelector from "./DataTypeSelector";
import { useHumanInputConfiguration } from "./hooks";
import HumanInputPreview from "./HumanInputPreview";
import SliderConfiguration from "./SliderConfiguration";
import UIComponentSelector from "./UIComponentSelector";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { HumanConfiguration } from "@/types/evaluate";
import { BlockEnv, BlockProps } from "@/components/PowerBlock/Block";
import { withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { custom, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";

type SmartTableHumanInputComponent = "slider" | "rating" | "choice";

interface SmartTableControlPickerProps {
  selectedComponent?: string;
  disabled?: boolean;
  onChange: (component: SmartTableHumanInputComponent) => void;
}

const SmartTableControlPicker = ({
  selectedComponent,
  disabled = false,
  onChange,
}: SmartTableControlPickerProps) => (
  <div className="grid gap-2">
    {SMART_TABLE_HUMAN_INPUT_CONTROLS.map(control => {
      const isSelected = selectedComponent === control.component;

      return (
        <button
          key={control.component}
          type="button"
          disabled={disabled}
          className={cn(
            "group rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            isSelected
              ? "border-blue-200 bg-blue-50/70 text-foreground"
              : "border-border/70 bg-background hover:border-border hover:bg-muted/35",
          )}
          onClick={() => onChange(control.component)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-0.5">
              <div className="text-sm font-medium leading-5">{control.label}</div>
              <div className="text-xs leading-4 text-muted-foreground">{control.description}</div>
            </div>
            <div
              className={cn(
                "mt-0.5 h-2 w-2 shrink-0 rounded-full transition-colors",
                isSelected
                  ? "bg-blue-500"
                  : "bg-muted-foreground/20 group-hover:bg-muted-foreground/35",
              )}
              aria-hidden="true"
            />
          </div>
        </button>
      );
    })}
  </div>
);

const validationSchema = createBlockSchema<HumanConfiguration>()
  .requireName()
  .addRule(required("configuration.data_type", "Data type is required"))
  .addRule(required("configuration.ui_element.component", "UI component is required"))
  .addRule(
    custom(
      "configuration.ui_element",
      (value, payload) => {
        if (
          payload.configuration?.ui_element?.component === "slider" &&
          payload.configuration?.data_type === "number" &&
          value.min !== undefined &&
          value.max !== undefined
        ) {
          return value.min < value.max;
        }
        return true;
      },
      "Minimum value must be less than maximum value",
      ["configuration.ui_element.min", "configuration.ui_element.max"],
    ),
  )
  .build();

const HumanInputBlockCore = observer(
  (props: Omit<BlockProps<HumanConfiguration>, "DefaultFields">) => {
    const {
      configuration,
      dataType,
      uiComponent,
      sliderConfig,
      ratingConfig,
      choiceConfig,
      handleDataTypeChange,
      handleUiComponentChange,
      handleSliderConfigChange,
      handleRatingConfigChange,
      handleChoiceOptionChange,
      handleAddChoiceOption,
      handleRemoveChoiceOption,
      handleSmartTableComponentChange,
      ensureSmartTableConfiguration,
    } = useHumanInputConfiguration();
    const isReadonly = useIsReadonly();
    const isSmartTable = props.env === BlockEnv.SMART_TABLE;

    useEffect(() => {
      if (!isSmartTable || isReadonly) {
        return;
      }

      ensureSmartTableConfiguration();
    }, [ensureSmartTableConfiguration, isReadonly, isSmartTable]);

    return (
      <>
        <SectionGroup
          title={isSmartTable ? "Manual input" : "Input Configuration"}
          description={
            isSmartTable
              ? "Add a fast, cell-native control for human judgment."
              : "Configure the type and appearance of human input"
          }
          icon={<UserIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            {isSmartTable ? (
              <>
                <SmartTableControlPicker
                  selectedComponent={uiComponent}
                  disabled={isReadonly}
                  onChange={handleSmartTableComponentChange}
                />

                {ratingConfig ? (
                  <ValidatedField name="Rating scale" path="configuration.ui_element.max">
                    <div className="space-y-1.5 rounded-lg border border-border/70 bg-muted/20 p-3">
                      <label className="text-xs font-medium text-muted-foreground">
                        Maximum rating
                      </label>
                      <Input
                        type="number"
                        min={2}
                        max={10}
                        value={ratingConfig.max}
                        disabled={isReadonly}
                        className="h-8"
                        onChange={event => handleRatingConfigChange(event.target.value)}
                      />
                    </div>
                  </ValidatedField>
                ) : null}

                {choiceConfig ? (
                  <ValidatedField name="Choices" path="configuration.ui_element.options">
                    <div className="space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          Options
                          <span className="ml-1 font-normal text-muted-foreground/70">
                            ({choiceConfig.options.length})
                          </span>
                        </label>
                        <Button
                          type="button"
                          size="tinyButton"
                          variant="ghost"
                          disabled={isReadonly}
                          className="h-6 px-1.5 text-[11px]"
                          onClick={handleAddChoiceOption}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add
                        </Button>
                      </div>
                      <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                        {choiceConfig.options.map((option, index) => (
                          <div key={option} className="flex items-center gap-1.5">
                            <Input
                              value={option}
                              disabled={isReadonly}
                              className="h-8"
                              onChange={event =>
                                handleChoiceOptionChange(index, event.target.value)
                              }
                            />
                            <Button
                              type="button"
                              size="tinyIcon"
                              variant="ghost"
                              disabled={isReadonly || choiceConfig.options.length <= 2}
                              className="h-8 w-8 shrink-0 text-muted-foreground"
                              onClick={() => handleRemoveChoiceOption(index)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </ValidatedField>
                ) : null}
              </>
            ) : (
              <>
                <ValidatedField name="Data Type" path="configuration.data_type">
                  <DataTypeSelector
                    dataType={dataType}
                    onDataTypeChange={handleDataTypeChange}
                    isValid // ValidatedField handles validation display
                    disabled={isReadonly}
                  />
                </ValidatedField>

                {(() => {
                  const uiComponentOptions = dataType
                    ? HUMAN_INPUT_DATA_TYPE[dataType]?.component_options || []
                    : [];

                  if (uiComponentOptions.length === 0) {
                    return null;
                  }

                  return (
                    <ValidatedField name="UI Component" path="configuration.ui_element.component">
                      <UIComponentSelector
                        dataType={dataType}
                        uiComponent={uiComponent}
                        onUiComponentChange={handleUiComponentChange}
                        disabled={isReadonly}
                      />
                    </ValidatedField>
                  );
                })()}
              </>
            )}

            {sliderConfig && (
              <ValidatedField name="Slider Configuration" path="configuration.ui_element">
                <SliderConfiguration
                  sliderConfig={sliderConfig}
                  onSliderConfigChange={handleSliderConfigChange}
                  disabled={isReadonly}
                />
              </ValidatedField>
            )}
          </div>
        </SectionGroup>

        {configuration && (
          <SectionGroup
            title="Preview"
            description="Preview how the input will appear to users"
            variant="compact"
            icon={<AdjustmentsIcon className="h-4 w-4" />}
          >
            <HumanInputPreview configuration={configuration} />
          </SectionGroup>
        )}
      </>
    );
  },
);

export default withBlockStore<HumanConfiguration>(HumanInputBlockCore, {
  validationSchema,
});
